import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { CompanyFunction, IUserCompanyFunction } from '@condo/domain';
import { groupBy } from 'lodash';
import { fetchUserCompanyFunctions } from '../api';
import { withGlobalErrorHandling } from '@/shared/lib/query-client';

export const userCompanyFunctionQueryKeys = {
    all: ['userCompanyFunctions'] as const,
    lists: () => [...userCompanyFunctionQueryKeys.all, 'list'] as const,
    list: () => [...userCompanyFunctionQueryKeys.lists()] as const,
};

/**
 * Hook to fetch user company functions
 * Returns all user company function mappings
 */
export const useUserCompanyFunctions = () => {
    return useQuery({
        queryKey: userCompanyFunctionQueryKeys.list(),
        queryFn: async () => fetchUserCompanyFunctions(),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        ...withGlobalErrorHandling(),
    });
};

/**
 * Hook to get users grouped by company function
 * Similar to Vue store getter: userCompanyFunction/usersByCompanyFunctions
 * Returns Record<CompanyFunction, IUserCompanyFunction[]>
 */
export const useUsersByCompanyFunctions = () => {
    const { data: userCompanyFunctions = [], ...rest } = useUserCompanyFunctions();

    const usersByCompanyFunctions = useMemo(() => {
        return groupBy(userCompanyFunctions, (u: IUserCompanyFunction) => u.companyFunction) as Partial<Record<CompanyFunction, IUserCompanyFunction[]>>;
    }, [userCompanyFunctions]);

    return {
        usersByCompanyFunctions,
        ...rest,
    };
};

/**
 * Hook to get eligible user IDs for each company function
 * Filters to only include users with contacts (similar to Vue eligibleCompanyFunctionUsers)
 * Returns Record<CompanyFunction, string[]> where values are user IDs
 */
export const useEligibleCompanyFunctionUsers = () => {
    const { data: userCompanyFunctions = [], ...rest } = useUserCompanyFunctions();

    const eligibleCompanyFunctionUsers = useMemo(() => {
        const grouped = groupBy(userCompanyFunctions, (u: IUserCompanyFunction) => u.companyFunction) as Partial<
            Record<CompanyFunction, IUserCompanyFunction[]>
        >;

        const result: Record<CompanyFunction, string[]> = {} as Record<CompanyFunction, string[]>;
        for (const [companyFunction, functions] of Object.entries(grouped)) {
            result[companyFunction as CompanyFunction] = functions.filter(fn => Boolean(fn.contact)).map(fn => fn.userId);
        }
        return result;
    }, [userCompanyFunctions]);

    return {
        eligibleCompanyFunctionUsers,
        ...rest,
    };
};
